import React, { useEffect, useState } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Container,
  Typography,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import { motion, useReducedMotion } from "framer-motion";
import { useSnackbar } from "notistack";
import ProgressStepper from "bundles/common/components/ProgressStepper";
import OnboardingSplash from "./OnboardingSplash";
import { useTheme } from "@mui/material/styles";
import sh from "bundles/common/utils/sh";
import {
  uploadResume,
  attachResumeToCandidate,
} from "bundles/common/utils/uploadDocument";
import StepJobSearch from "./OnboardingSteps/StepJobSearch";
import StepAboutYou from "./OnboardingSteps/StepAboutYou";
import StepLocation from "./OnboardingSteps/StepLocation";
import StepResume from "./OnboardingSteps/StepResume";
import StepCongrats from "./OnboardingSteps/StepCongrats";

// Step 2 used to hold 7 required fields on one screen — the heaviest drop-off
// point of the funnel. It is now split into "about you" and "location".
const onboardingSteps = {
  1: ["availability", "desired_sector"],
  2: ["candidate_status", "experience_year", "job_title", "highest_degree"],
  3: ["country", "region", "work_permit"],
  4: ["resume_url"],
  5: [],
};

const RESUME_STEP = 4;
const CONGRATS_STEP = 5;

const stepLabels = {
  1: "onboarding.job_search",
  2: "onboarding.about_you",
  3: "onboarding.location",
  4: "onboarding.difference",
  5: "onboarding.challenge",
};

const stepWhy = {
  1: "onboarding.why_job_search",
  2: "onboarding.why_about_you",
  3: "onboarding.why_location",
  4: "onboarding.why_resume",
};

const OnboardingPage = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"), { noSsr: true });
  const currentCandidate = useSelector((state) => state.current_candidate);
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  // Step 1 starts blank on purpose: the account may carry defaults (e.g.
  // availability "listening"), and a preselected answer biases the funnel.
  const initialFormValues = {
    availability: "",
    desired_sector: [],
    candidate_status: currentCandidate.candidate_status || "",
    experience_year: currentCandidate.experience_year || "",
    job_title: currentCandidate.job_title || "",
    highest_degree: currentCandidate.highest_degree || "",
    work_permit: currentCandidate.work_permit || "",
    resume_url: currentCandidate.resume_url || "",
    country: currentCandidate.country || "",
    region: currentCandidate.region || "",
  };

  const [values, setValues] = useState(initialFormValues);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [resume, setResume] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [recsLoading, setRecsLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [displayableSteps, setDisplayableSteps] = useState(
    Object.keys(onboardingSteps)
  );
  const [justSignedUp] = useState(
    () =>
      new URLSearchParams(window.location.search).get("justSignedUp") ===
      "true"
  );
  const shouldReduceMotion = useReducedMotion();
  const splashEnabled = justSignedUp && !shouldReduceMotion && !isDesktop;
  const [showSplash, setShowSplash] = useState(splashEnabled);

  const revealProps = (delay) => ({
    initial: splashEnabled ? { opacity: 0, y: 12 } : false,
    animate: { opacity: 1, y: 0 },
    transition: { delay, duration: 0.4, ease: "easeOut" },
  });

  useEffect(() => {
    if (!justSignedUp) return;
    const url = new URL(window.location.href);
    url.searchParams.delete("justSignedUp");
    window.history.replaceState({}, "", url);
  }, []);

  const welcomeLabel = () => {
    if (currentStep < CONGRATS_STEP) {
      const currentHours = new Date().getHours();
      if (currentHours < 12) return intl.formatMessage({ id: "good_morning" });
      if (currentHours < 18) return intl.formatMessage({ id: "good_afternoon" });
      return intl.formatMessage({ id: "good_evening" });
    }
    return intl.formatMessage({ id: "good_job" });
  };

  useEffect(() => {
    const uncompletedSteps = Object.keys(onboardingSteps).filter((step) =>
      onboardingSteps[step].some(isAttributeEmpty)
    );
    setDisplayableSteps(
      uncompletedSteps.concat([Object.keys(onboardingSteps).at(-1)])
    );
    const firstStep = parseInt(
      uncompletedSteps.length > 0 ? uncompletedSteps[0] : Object.keys(onboardingSteps).at(-1)
    );
    setCurrentStep(firstStep);
    window.history.replaceState({ onboardingStep: firstStep }, "", window.location.href);
  }, []);

  // Each step is a history entry, so the phone back gesture goes to the previous step.
  useEffect(() => {
    const onPopState = (e) => {
      const step = e.state?.onboardingStep;
      if (!step) return;
      if (currentStep === CONGRATS_STEP) {
        window.history.pushState({ onboardingStep: CONGRATS_STEP }, "", window.location.href);
        return;
      }
      if (!displayableSteps.includes(`${step}`)) return;
      setCurrentStep(step);
      window.scrollTo({ top: 0, behavior: "smooth" });
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [currentStep, displayableSteps]);

  useEffect(() => {
    if (currentStep === CONGRATS_STEP) {
      setRecsLoading(true);
      sh.get("/job_recommendations", {
        params: { id: currentCandidate.id },
      })
        .then((res) => {
          if (res?.data) setRecommendations(res.data);
        })
        .catch((err) => console.error(err))
        .finally(() => {
          setRecsLoading(false);
          setLoading(false);
        });
    }
  }, [currentStep]);

  const isAttributeEmpty = (attr) => {
    return values[attr] == null || values[attr] === "";
  };

  const handleCardSelected = (name, value, multiple) => {
    if (multiple) {
      const current = values[name] || [];
      handleChange(
        name,
        current.includes(value) ? current.filter((c) => c !== value) : [...current, value]
      );
    } else {
      handleChange(name, value);
    }
  };

  const handleInputValue = (e) => {
    const { name, value } = e.target;
    handleChange(name, value);
  };

  const handleChange = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    validate({ [name]: value });
  };

  // Clears a field without running the "required" validation — used when a
  // country change resets the region, which is not a user mistake.
  const resetField = (name) => {
    setValues((prev) => ({ ...prev, [name]: "" }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = (fieldValues = values) => {
    const temp = { ...errors };
    const required = <FormattedMessage id="form_validation.required" />;

    if ("availability" in fieldValues)
      temp.availability = fieldValues.availability ? "" : required;
    if ("desired_sector" in fieldValues)
      temp.desired_sector = fieldValues.desired_sector?.length > 0 ? "" : required;
    if ("candidate_status" in fieldValues)
      temp.candidate_status = fieldValues.candidate_status ? "" : required;
    if ("experience_year" in fieldValues)
      temp.experience_year = fieldValues.experience_year ? "" : required;
    if ("job_title" in fieldValues)
      temp.job_title = fieldValues.job_title ? "" : required;
    if ("highest_degree" in fieldValues)
      temp.highest_degree = fieldValues.highest_degree ? "" : required;
    if ("resume_url" in fieldValues)
      temp.resume_url = resume.length > 0 || values.resume_url ? "" : required;
    if ("country" in fieldValues)
      temp.country = fieldValues.country ? "" : required;
    if ("region" in fieldValues)
      temp.region = fieldValues.region ? "" : required;

    setErrors({ ...temp });
    return Object.values(temp).every((x) => x === "");
  };

  // On a phone the failed field is often above the fold — without this the
  // button just looks broken.
  const scrollToFirstInvalid = () => {
    const firstInvalid = onboardingSteps[currentStep].find(isAttributeEmpty);
    if (!firstInvalid) return;
    document
      .getElementById(`ob-field-${firstInvalid}`)
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const notifySaveFailed = () => {
    enqueueSnackbar(
      intl.formatMessage({
        id: "candidate.dashboard.action_failed",
        defaultMessage: "Une erreur est survenue. Réessayez.",
      }),
      { variant: "error" }
    );
  };

  const handleNextClicked = () => {
    setLoading(true);
    if (currentStep === RESUME_STEP) {
      if (resume.length > 0) {
        uploadResume(resume[0])
          .then((res) =>
            attachResumeToCandidate({
              id: currentCandidate.id,
              authenticity_token: authenticityToken,
              blob_signed_id: res.blob_signed_id,
            })
          )
          .then(() => updateCandidate())
          .catch((err) => {
            // Without this catch a flaky upload left the button spinning forever.
            console.error(err);
            notifySaveFailed();
            setLoading(false);
          });
      } else {
        validate({ resume_url: values.resume_url });
        scrollToFirstInvalid();
        setLoading(false);
      }
    } else {
      updateCandidate();
    }
  };

  const updateCandidate = () => {
    const currentStepAttributes = onboardingSteps[currentStep];
    const candidate = {};
    currentStepAttributes.forEach((attr) => {
      candidate[attr] = values[attr];
    });

    if (validate(candidate)) {
      sh.put("candidate", {
        authenticity_token: authenticityToken,
        id: currentCandidate.id,
        candidate,
      })
        .then(() => {
          if (
            candidate.work_permit === false &&
            currentCandidate.whitelisted === true
          ) {
            sh.post("/candidate/blacklist", {
              authenticity_token: authenticityToken,
              id: currentCandidate.id,
              candidate,
            }).catch((err) => console.error(err));
          }
          goNextStep();
        })
        .catch((err) => {
          console.error(err);
          notifySaveFailed();
        })
        .finally(() => setLoading(false));
    } else {
      scrollToFirstInvalid();
      setLoading(false);
    }
  };

  const goNextStep = () => {
    const currentStepIndex = displayableSteps.indexOf(`${currentStep}`);
    const nextStep = parseInt(displayableSteps[currentStepIndex + 1]);
    setCurrentStep(nextStep);
    window.history.pushState({ onboardingStep: nextStep }, "", window.location.href);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const currentStepIndex = displayableSteps.indexOf(`${currentStep}`);
  const hasPrevStep = currentStepIndex > 0 && currentStep < CONGRATS_STEP;
  const isLastFormStep =
    parseInt(displayableSteps[currentStepIndex + 1]) === CONGRATS_STEP;

  // Goes through history so the button and the back gesture stay in sync.
  const goPrevStep = () => {
    if (!hasPrevStep) return;
    window.history.back();
  };

  // The resume step is satisfied by a freshly picked file too — it only becomes
  // a resume_url after the upload that happens on "next".
  const isStepComplete = () => {
    if (currentStep === RESUME_STEP)
      return resume.length > 0 || !!values.resume_url;
    return (onboardingSteps[currentStep] || []).every(
      (attr) => !isAttributeEmpty(attr)
    );
  };

  const nextButton = (
    <Button
      disabled={loading || !isStepComplete()}
      onClick={handleNextClicked}
      sx={{
        width: isDesktop ? "240px" : "100%",
        height: "3.25rem",
        fontSize: "0.875rem",
        fontWeight: 700,
        fontFamily:
          "'Segoe UI Semibold', 'Segoe UI', system-ui, sans-serif",
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        borderRadius: "9999px",
        bgcolor: "hsl(153, 68%, 25%)",
        color: "hsl(0, 0%, 100%)",
        "&:hover": { bgcolor: "hsl(153, 68%, 22%)" },
        "&.Mui-disabled": {
          bgcolor: "hsl(153, 68%, 25%)",
          color: "rgba(255,255,255,0.5)",
          opacity: 0.6,
        },
      }}
    >
      {loading ? (
        <CircularProgress size={20} sx={{ color: "hsl(0, 0%, 100%)" }} />
      ) : (
        <FormattedMessage id={isLastFormStep ? "onboarding.finish" : "next"} />
      )}
    </Button>
  );

  const secondaryLinkSx = {
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    fontSize: "0.8125rem",
    color: "hsl(215.4, 16.3%, 46.9%)",
    textTransform: "none",
    "&:hover": { color: "hsl(153, 68%, 25%)", bgcolor: "transparent" },
  };

  return (
    <Container
      maxWidth="md"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
        pt: isDesktop ? "3rem" : "1.5rem",
        pb: currentStep < CONGRATS_STEP && !isDesktop
          ? "7rem"
          : (isDesktop ? "3rem" : "1.5rem"),
        px: isDesktop ? "2rem" : "1rem",
      }}
    >
      {showSplash && (
        <OnboardingSplash onFinish={() => setShowSplash(false)} />
      )}

      {/* Header */}
      <motion.div {...revealProps(1.55)}>
        <Box sx={{ textAlign: "center" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "baseline",
              gap: "0.5rem",
              flexWrap: "wrap",
            }}
          >
            <Typography
              component="span"
              sx={{
                fontFamily:
                  "'Segoe UI Black', 'Segoe UI', system-ui, sans-serif",
                fontSize: isDesktop ? "1.875rem" : "1.25rem",
                lineHeight: 1.2,
                color: "hsl(153, 68%, 25%)",
              }}
            >
              {welcomeLabel()}
              <Box component="span" sx={{ color: "hsl(222.2, 84%, 4.9%)" }}>,</Box>
            </Typography>
            <Typography
              component="span"
              sx={{
                fontFamily:
                  "'Segoe UI Black', 'Segoe UI', system-ui, sans-serif",
                fontSize: isDesktop ? "1.875rem" : "1.25rem",
                lineHeight: 1.2,
                color: "hsl(222.2, 84%, 4.9%)",
              }}
            >
              {currentCandidate.first_name}{" "}
              {currentStep === CONGRATS_STEP ? "👌" : "👋"}
            </Typography>
          </Box>

          {/* Why this step is worth 30 seconds of the candidate's time */}
          {currentStep < CONGRATS_STEP && (
            <Typography
              sx={{
                mt: "0.375rem",
                fontFamily: "'Segoe UI', system-ui, sans-serif",
                fontSize: "0.875rem",
                color: "hsl(215.4, 16.3%, 46.9%)",
              }}
            >
              {intl.formatMessage({ id: stepWhy[currentStep] })}
            </Typography>
          )}

          {(currentStep === 1 || currentStep === RESUME_STEP) && (
            <Box
              sx={{
                mt: "0.5rem",
                display: "inline-flex",
                alignItems: "center",
                px: "0.75rem",
                py: "0.25rem",
                borderRadius: "9999px",
                bgcolor: "hsla(153, 68%, 25%, 0.08)",
                color: "hsl(153, 68%, 25%)",
                fontFamily: "'Segoe UI Semibold', system-ui, sans-serif",
                fontSize: "0.75rem",
              }}
            >
              {intl.formatMessage({
                id: currentStep === 1 ? "onboarding.time_estimate" : "onboarding.last_step",
              })}
            </Box>
          )}
        </Box>
      </motion.div>

      {/* Stepper */}
      <motion.div {...revealProps(1.63)}>
        <Box sx={{ width: isDesktop ? "70%" : "100%", mx: "auto" }}>
          <ProgressStepper
            steps={displayableSteps}
            activeStep={
              currentStep === CONGRATS_STEP
                ? displayableSteps.length
                : currentStepIndex
            }
            labels={stepLabels}
            onStepClick={(stepNum) => {
              setCurrentStep(stepNum);
              window.history.replaceState({ onboardingStep: stepNum }, "", window.location.href);
            }}
          />
        </Box>
      </motion.div>

      {/* Step content */}
      <motion.div {...revealProps(1.71)}>
        {currentStep === 1 && (
          <StepJobSearch
            values={values}
            errors={errors}
            handleCardSelected={handleCardSelected}
          />
        )}
        {currentStep === 2 && (
          <StepAboutYou
            values={values}
            errors={errors}
            handleCardSelected={handleCardSelected}
            handleInputValue={handleInputValue}
          />
        )}
        {currentStep === 3 && (
          <StepLocation
            values={values}
            errors={errors}
            handleCardSelected={handleCardSelected}
            handleInputValue={handleInputValue}
            resetField={resetField}
          />
        )}
        {currentStep === RESUME_STEP && (
          <StepResume
            values={values}
            errors={errors}
            setErrors={setErrors}
            resume={resume}
            setResume={setResume}
          />
        )}
        {currentStep === CONGRATS_STEP && (
          <StepCongrats recommendations={recommendations} loading={recsLoading} />
        )}
      </motion.div>

      {/* Secondary links right below the step content, away from the CTA */}
      {currentStep < CONGRATS_STEP && (hasPrevStep || currentStep === RESUME_STEP) && (
        <motion.div {...revealProps(1.75)}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: "-0.5rem" }}>
            <Box>
              {hasPrevStep && (
                <Button disableRipple onClick={goPrevStep} sx={{ ...secondaryLinkSx, px: 0, minWidth: 0 }}>
                  <FormattedMessage id="onboarding.back" defaultMessage="Retour" />
                </Button>
              )}
            </Box>
            {currentStep === RESUME_STEP && (
              <Button disableRipple onClick={goNextStep} sx={{ ...secondaryLinkSx, textDecoration: "underline" }}>
                <FormattedMessage id="skip_step" />
              </Button>
            )}
          </Box>
        </motion.div>
      )}

      {/* Navigation */}
      {currentStep < CONGRATS_STEP && (
        <motion.div {...revealProps(1.79)}>
          {isDesktop ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: "0.5rem",
              }}
            >
              {nextButton}
            </Box>
          ) : (
            // Mobile: the primary action never scrolls out of reach.
            <Box
              sx={{
                position: "fixed",
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 30,
                bgcolor: "hsl(0, 0%, 100%)",
                borderTop: "1px solid hsl(214.3, 31.8%, 91.4%)",
                px: "1rem",
                pt: "0.75rem",
                pb: "calc(0.75rem + env(safe-area-inset-bottom, 0px))",
              }}
            >
              {nextButton}
            </Box>
          )}
        </motion.div>
      )}
    </Container>
  );
};

export default OnboardingPage;
